import { useCallback, useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  AlertTriangle,
  Check,
  Copy,
  CreditCard,
  Loader2,
  RefreshCw,
  Store,
} from 'lucide-react';
import { db, functions } from '../../config/firebase';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import type {
  ExternalStripeStatus,
  PaymentSignal,
  SquareInventoryMode,
  SquareStatus,
  StockLocation,
} from '../../types/stock';

const MODE_COPY: Record<SquareInventoryMode, { label: string; detail: string }> = {
  counts: {
    label: 'Square inventory counts (recommended)',
    detail:
      'FASH mirrors the stock number Square reports. Handles sales, refunds and recounts in one signal.',
  },
  orders: {
    label: 'Square order lines',
    detail:
      'For sellers who do not track inventory in Square. Each completed sale subtracts from FASH.',
  },
  off: {
    label: 'Detect sales only',
    detail: 'FASH records Square sales for your records but never changes stock.',
  },
};

function money(cents: number, currency: string): string {
  const sign = cents < 0 ? '-' : '';
  return `${sign}${currency === 'USD' ? '$' : ''}${(Math.abs(cents) / 100).toFixed(2)}`;
}

export function PaymentSyncSettings({ businessId }: { businessId: string }) {
  const [square, setSquare] = useState<SquareStatus | null>(null);
  const [stripe, setStripe] = useState<ExternalStripeStatus | null>(null);
  const [locations, setLocations] = useState<StockLocation[]>([]);
  const [signals, setSignals] = useState<PaymentSignal[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showStripeForm, setShowStripeForm] = useState(false);
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [stripeLocationId, setStripeLocationId] = useState('');

  const refresh = useCallback(async () => {
    try {
      const [sq, st, sig] = await Promise.all([
        httpsCallable(functions, 'getSquareStatus')({ businessId }),
        httpsCallable(functions, 'getExternalStripeStatus')({ businessId }),
        httpsCallable(functions, 'listPaymentSignals')({ businessId, needsReviewOnly: true }),
      ]);
      setSquare(sq.data as SquareStatus);
      setStripe(st.data as ExternalStripeStatus);
      setSignals(((sig.data as any)?.signals || []) as PaymentSignal[]);
    } catch (err: any) {
      setError(err?.message || 'Could not load payment integrations');
    }
  }, [businessId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'stock_locations'), where('businessId', '==', businessId)),
      (snap) => {
        const rows = snap.docs
          .map((d) => d.data() as StockLocation)
          .filter((l) => l.active !== false)
          .sort((a, b) => Number(!!b.isDefault) - Number(!!a.isDefault));
        setLocations(rows);
        setStripeLocationId((current) => current || rows[0]?.locationId || '');
      },
    );
    return unsub;
  }, [businessId]);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    setMessage(null);
    try {
      await fn();
    } catch (err: any) {
      setError(err?.message || 'Something went wrong');
    }
    setBusy(null);
  };

  const connectSquare = () =>
    run('square-connect', async () => {
      const res: any = await httpsCallable(functions, 'squareAuthStart')({ businessId });
      window.open(res.data.authUrl, '_blank', 'noopener,noreferrer');
      setMessage('Approve FASH in the Square window, then refresh this page.');
    });

  const setMode = (mode: SquareInventoryMode) =>
    run('square-mode', async () => {
      await httpsCallable(functions, 'setSquareInventoryMode')({ businessId, mode });
      await refresh();
    });

  const disconnectSquare = () =>
    run('square-disconnect', async () => {
      await httpsCallable(functions, 'squareDisconnect')({ businessId });
      await refresh();
      setMessage('Square disconnected. Those locations are free for another source.');
    });

  const connectStripe = () =>
    run('stripe-connect', async () => {
      const res: any = await httpsCallable(functions, 'connectExternalStripe')({
        businessId,
        secretKey: secretKey.trim(),
        webhookSecret: webhookSecret.trim(),
        locationId: stripeLocationId,
      });
      setSecretKey('');
      setWebhookSecret('');
      setShowStripeForm(false);
      await refresh();
      setMessage(`Add this endpoint in your Stripe dashboard: ${res.data.webhookUrl}`);
    });

  const disconnectStripe = () =>
    run('stripe-disconnect', async () => {
      await httpsCallable(functions, 'disconnectExternalStripe')({ businessId });
      await refresh();
    });

  const dismissSignal = (signalId: string) =>
    run(`signal-${signalId}`, async () => {
      await httpsCallable(functions, 'resolvePaymentSignal')({ signalId, note: 'No stock moved' });
      setSignals((prev) => prev.filter((s) => s.signalId !== signalId));
    });

  return (
    <div className="space-y-4">
      {/* ── Square ─────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <Store className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="text-sm font-medium text-heading">Square</p>
                <p className="text-xs text-body">
                  Best option if you sell in person or online with Square. Square sales carry the
                  item they sold, so FASH can keep your stock honest without a hold.
                </p>
              </div>
            </div>
            {square?.connected ? (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 shrink-0">
                Connected
              </span>
            ) : null}
          </div>

          {!square?.connected ? (
            <Button type="button" size="sm" disabled={busy === 'square-connect'} onClick={connectSquare}>
              {busy === 'square-connect' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
              Connect Square
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="space-y-2">
                {(Object.keys(MODE_COPY) as SquareInventoryMode[]).map((mode) => (
                  <label
                    key={mode}
                    className="flex items-start gap-2 text-xs cursor-pointer rounded-lg border border-gray-100 p-2"
                  >
                    <input
                      type="radio"
                      className="mt-0.5"
                      checked={(square.inventoryMode || 'counts') === mode}
                      disabled={busy === 'square-mode'}
                      onChange={() => void setMode(mode)}
                    />
                    <span>
                      <span className="font-medium text-heading">{MODE_COPY[mode].label}</span>
                      <br />
                      <span className="text-body">{MODE_COPY[mode].detail}</span>
                    </span>
                  </label>
                ))}
              </div>

              {square.mappedLocations && square.mappedLocations.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[11px] font-medium text-heading uppercase tracking-wider">
                    Mapped locations
                  </p>
                  {square.mappedLocations.map((loc) => (
                    <p key={loc.locationId} className="text-[11px] text-body">
                      {loc.name} ·{' '}
                      {loc.deliveryZoneEligible && loc.sellableOnFash
                        ? `sellable on FASH, buffer ${loc.safetyBuffer}`
                        : 'not yet sellable on FASH'}
                    </p>
                  ))}
                  {square.mappedLocations.some((l) => !l.deliveryZoneEligible) && (
                    <p className="text-[11px] text-amber-700">
                      Confirm a location is inside a FASH delivery zone under Catalog → Stock
                      locations before FASH will sell from it.
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy === 'square-disconnect'}
                  onClick={disconnectSquare}
                >
                  Disconnect
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => void refresh()}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1" />
                  Refresh
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── The merchant's own Stripe ───────────────────────────── */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <CreditCard className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="text-sm font-medium text-heading">Your own Stripe account</p>
                <p className="text-xs text-body">
                  For a Wix, Squarespace or custom site. FASH only adjusts stock when the charge says
                  which SKU sold — an amount-only charge is flagged for you instead of guessed at.
                </p>
              </div>
            </div>
            {stripe?.connected ? (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 shrink-0">
                Connected
              </span>
            ) : null}
          </div>

          {stripe?.connected ? (
            <div className="space-y-2">
              {stripe.webhookUrl && (
                <div className="flex items-center gap-2">
                  <code className="text-[10px] bg-gray-50 rounded px-2 py-1 flex-1 truncate">
                    {stripe.webhookUrl}
                  </code>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      void navigator.clipboard.writeText(stripe.webhookUrl!);
                      setMessage('Endpoint copied. Add it in Stripe → Developers → Webhooks.');
                    }}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
              <p className="text-[11px] text-body">
                Send these events: checkout.session.completed, payment_intent.succeeded,
                charge.succeeded, charge.refunded.
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy === 'stripe-disconnect'}
                onClick={disconnectStripe}
              >
                Disconnect
              </Button>
            </div>
          ) : showStripeForm ? (
            <div className="space-y-2">
              <Input
                placeholder="Restricted key (rk_live_…)"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
              />
              <Input
                placeholder="Webhook signing secret (whsec_…)"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
              />
              <select
                className="w-full text-sm border rounded-md h-9 px-2"
                value={stripeLocationId}
                onChange={(e) => setStripeLocationId(e.target.value)}
              >
                {locations.map((l) => (
                  <option key={l.locationId} value={l.locationId}>
                    Ships from {l.name}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-body">
                Use a <strong>restricted</strong> key with read access to charges, checkout sessions,
                products and prices. FASH never writes to your Stripe account.
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={busy === 'stripe-connect' || !secretKey || !webhookSecret}
                  onClick={connectStripe}
                >
                  {busy === 'stripe-connect' ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : null}
                  Connect
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setShowStripeForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button type="button" size="sm" variant="outline" onClick={() => setShowStripeForm(true)}>
              Connect Stripe
            </Button>
          )}
        </CardContent>
      </Card>

      {/* ── Unmatched sales ────────────────────────────────────── */}
      {signals.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-heading">
                  Sales with no stock update ({signals.length})
                </p>
                <p className="text-xs text-body">
                  Money moved on your own store but nothing explained which item left the shelf.
                  Import the SKU or log the movement so FASH does not oversell it.
                </p>
              </div>
            </div>

            <div className="divide-y rounded-lg border border-gray-100">
              {signals.map((signal) => (
                <div key={signal.signalId} className="px-3 py-2 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-heading">
                      {money(signal.amountCents, signal.currency)} ·{' '}
                      {signal.provider === 'square' ? 'Square' : 'Stripe'}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={busy === `signal-${signal.signalId}`}
                      onClick={() => void dismissSignal(signal.signalId)}
                    >
                      <Check className="h-3.5 w-3.5 mr-1" />
                      No stock moved
                    </Button>
                  </div>
                  <p className="text-[11px] text-body">
                    {signal.occurredAt ? new Date(signal.occurredAt).toLocaleString() : 'Unknown time'}
                    {signal.lines.length
                      ? ` · ${signal.lines.map((l) => l.sku || 'unknown SKU').join(', ')}`
                      : ' · no item information on the payment'}
                  </p>
                  {signal.note && <p className="text-[11px] text-body">{signal.note}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {message && <p className="text-xs text-green-700 break-all">{message}</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
